import React from "react";
import { csvValue } from "./csvValue.js";

export function groupBy(items, getKey) {
  const map = new Map();
  (items || []).forEach((item) => {
    const key = getKey(item) || "Other";
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(item);
  });
  return map;
}

export function indexById(items = []) {
  return new Map(items.map((item) => [String(item.id), item]));
}

export function findById(items = [], id) {
  if (id === undefined || id === null) return undefined;
  return items.find((item) => String(item.id) === String(id));
}

// Two employment types, deliberately. "Intern" is the retired word -- stored
// records still carry it, so isJuniorType matches both while only "Junior"
// is ever offered or displayed. The one place this list and this check are
// defined; every screen that needs either imports from here instead of
// re-deriving its own copy (that drift is exactly how four separately
// maintained classifiers happened in the first place).
export const EMPLOYMENT_TYPE_OPTIONS = ["Full-Time", "Junior"];
export const isJuniorType = (v) => /intern|junior/i.test(String(v || ""));

function entityTenantId(entity = {}) {
  return entity.tenant_id ?? entity.tenantId ?? entity.tenant ?? "";
}

function entityWorkspaceId(entity = {}) {
  return entity.workspace_id ?? entity.workspaceId ?? entity.workspace ?? "";
}

export function resolveActiveEmployee(data = {}) {
  const allEmployees = data.employees || [];
  const me = data.me || {};
  const user = me.user || me.account || me || {};
  const activeTenantId = me.activeTenant?.id || me.tenant?.id || me.tenant || "";
  const activeWorkspaceId = me.activeWorkspace?.id || me.workspace?.id || me.workspace || "";
  const seen = new Set();
  const scopedEmployees = ((me.employees || []).length ? me.employees : allEmployees.filter((item) => String(item.user) === String(user.id)))
    .map((item) => findById(allEmployees, item.id) || item)
    .filter((item) => {
      if (!item?.id) return false;
      const key = String(item.id);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

  if (activeWorkspaceId) {
    const workspaceMatch = scopedEmployees.find((item) => String(entityWorkspaceId(item)) === String(activeWorkspaceId) && (!activeTenantId || String(entityTenantId(item)) === String(activeTenantId)));
    if (workspaceMatch) return workspaceMatch;
  }

  if (activeTenantId) {
    const tenantMatch = scopedEmployees.find((item) => String(entityTenantId(item)) === String(activeTenantId));
    if (tenantMatch) return tenantMatch;
  }

  return scopedEmployees[0] || null;
}

export function filterForEmployee(tasks = [], employeeId) {
  if (!employeeId) return tasks;
  return tasks.filter((task) => String(task.owner) === String(employeeId) || String(task.owner_id) === String(employeeId) || String(task.assignee) === String(employeeId));
}

export function projectName(data, id) {
  return findById(data.projects || [], id)?.name || "";
}

export function employeeName(data, id) {
  if (!id) return "-";
  return findById(data.employees || [], id)?.display_name || findById(data.employees || [], id)?.username || "-";
}

export function employeeContact(data, id) {
  const employee = findById(data.employees || [], id);
  return employee ? `${employee.email || ""} ${employee.contact_number || ""}` : "";
}

// Somebody's CURRENT pay: the most recent record by effective date.
//
// Pay is a dated history — a raise adds a record, it does not replace the old
// one — so "the pay record for this person" is never a single row and picking
// the first match in a list is picking whichever one the server happened to
// send first. That is how a raise that was correctly recorded still showed the
// old salary on screen. Screens asking "what does this person earn" want the
// latest; screens asking "what did this month pay" must ask the server, which
// resolves it against that month rather than guessing here.
export function latestPayProfile(payProfiles = [], employeeId) {
  if (!employeeId) return null;
  const mine = payProfiles.filter((p) => String(p.employee) === String(employeeId));
  if (!mine.length) return null;
  return mine.reduce((newest, candidate) => {
    const a = new Date(candidate.effective_at || 0).getTime();
    const b = new Date(newest.effective_at || 0).getTime();
    // Ties fall back to the higher id — the one recorded later.
    if (a > b) return candidate;
    if (a === b && Number(candidate.id || 0) > Number(newest.id || 0)) return candidate;
    return newest;
  });
}

export function avatar(name = "?") {
  return <span className="avatar">{String(name).trim().charAt(0).toUpperCase() || "?"}</span>;
}

export function isCompleted(status = "") {
  return ["completed", "complete", "done", "passed", "submitted", "closed"].includes(String(status).toLowerCase());
}

function boundedProgress(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  return Math.min(Math.max(number, 0), 100);
}

function progressFromStatus(status = "") {
  const normalized = String(status || "").toLowerCase();
  if (isCompleted(normalized)) return 100;
  if (normalized.includes("review") || normalized.includes("submit")) return 85;
  if (normalized.includes("progress") || normalized.includes("working")) return 60;
  if (normalized.includes("block") || normalized.includes("delay") || normalized.includes("hold")) return 30;
  if (normalized.includes("open") || normalized.includes("assign") || normalized.includes("sent")) return 20;
  return 0;
}

export function progressForTask(task) {
  const explicitProgress = boundedProgress(task?.metadata?.progress ?? task?.progress_percent ?? task?.progress ?? task?.percentage);
  if (explicitProgress !== null) return explicitProgress;
  return progressFromStatus(task?.status);
}

export function findDailyStatus(statuses = [], employeeId, iso) {
  return statuses.find((item) => String(item.employee) === String(employeeId) && String(item.status_date).slice(0, 10) === iso);
}

export function getAttendanceStatus(dailyStatus, leaveRequests, employeeId, dateIso) {
  const daily = findDailyStatus(dailyStatus || [], employeeId, dateIso);
  if (daily) return { type: "present", entry: daily };
  const onLeave = (leaveRequests || []).some((lr) =>
    String(lr.employee) === String(employeeId) &&
    String(lr.status).toLowerCase() === "approved" &&
    dateIso >= String(lr.starts_on).slice(0, 10) &&
    dateIso <= String(lr.ends_on).slice(0, 10),
  );
  if (onLeave) return { type: "leave", entry: null };
  return { type: "absent", entry: null };
}

export function lastDays(count) {
  const today = new Date();
  return Array.from({ length: count }, (_, index) => {
    const date = new Date(today);
    date.setDate(today.getDate() - (count - index - 1));
    return { iso: isoDate(date), label: String(date.getDate()) };
  });
}

const WEEKDAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Monday through Sunday of the CURRENT week (not a trailing 7-day window
// like lastDays) — the home page's week strip reads "what's ahead this
// week," which has to include days that haven't happened yet.
export function thisWeekDays() {
  const today = new Date();
  const todayIso = isoDate(today);
  const mondayOffset = (today.getDay() + 6) % 7; // Sun=0..Sat=6 -> days since this week's Monday
  const monday = new Date(today);
  monday.setDate(today.getDate() - mondayOffset);
  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(monday);
    date.setDate(monday.getDate() + index);
    const iso = isoDate(date);
    return { iso, dayName: WEEKDAY_NAMES[date.getDay()], dayNum: date.getDate(), isToday: iso === todayIso };
  });
}

export function calendarDays(monthDate) {
  const first = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const last = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
  const days = Array.from({ length: first.getDay() }, () => null);
  for (let day = 1; day <= last.getDate(); day += 1) {
    const date = new Date(monthDate.getFullYear(), monthDate.getMonth(), day);
    days.push({ day, iso: isoDate(date), future: date > new Date() });
  }
  return days;
}

export function isoDate(value) {
  // Use LOCAL Date Parts — NOT ToISOString() Which Returns UTC.
  // For IST (+5:30): New Date(2026,5,15).toISOString() → "2026-06-14T18:30:00Z" → "2026-06-14"
  // That Makes Every Calendar Cell And Leave Comparison Appear One Day Ahead.
  const y = value.getFullYear();
  const mo = String(value.getMonth() + 1).padStart(2, "0");
  const d = String(value.getDate()).padStart(2, "0");
  return `${y}-${mo}-${d}`;
}

// A moment in the recent past reads as "2h ago" / "Yesterday" — quicker to
// scan than "24 Sep, 14:32" and it needs no mental subtraction. Anything
// older (or in the future, or a date with no time in it) keeps the plain date.
export function recentPast(value, date) {
  if (typeof value === "string" && !value.includes("T") && !value.includes(" ")) return null;
  const mins = Math.floor((Date.now() - date.getTime()) / 60000);
  if (mins < 0) return null;
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  if (Math.round((startOfDay(new Date()) - startOfDay(date)) / 86400000) === 1) return "Yesterday";
  return null;
}

export function formatDate(value) {
  if (!value) return "None";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return recentPast(value, date) || date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

export function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return recentPast(value, date) || date.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

export function humanDate(value) {
  return value.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

// The plain number only; a negative amount (a recovery, a reversal, a balance owed) keeps its minus sign
// instead of being shown as 0.
export function money(value) {
  const number = Number(value || 0);
  return Number.isInteger(number) ? String(number) : number.toFixed(2);
}

export function toggleSet(source, value) {
  const next = new Set(source);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}

export function numberOrNull(value) {
  return value === undefined || value === null || value === "" ? null : Number(value);
}

export function getLeadRows(data) {
  const lmsRows = data.leadRows || [];
  if (lmsRows.length) return lmsRows;
  return (data.leadAccounts || []).map((lead) => ({
    ...lead,
    owner_id: lead.owner,
    owner_name: employeeName(data, lead.owner),
    notes_count: (data.leadNotes || []).filter((note) => String(note.lead) === String(lead.id)).length,
  }));
}

export function leadSource(lead = {}) {
  return lead.source || lead.origin || lead.metadata?.origin || "Unknown";
}

export function normalizeLeadSource(value = "") {
  const text = String(value || "").trim().toLowerCase();
  const compact = text.replace(/[\s_-]+/g, "");
  if (["ig", "interviewgod", "interview_god"].includes(compact) || text.includes("interviewgod")) return "InterviewGod";
  if (["insta", "instagram"].includes(compact)) return "Instagram";
  if (["l", "li", "in", "linkedin", "linked", "linkdin"].includes(compact) || text.includes("linked")) return "LinkedIn";
  if (["cw", "c", "client", "clientwebsite", "clientweb", "vi"].includes(compact) || text.includes("client")) return "Client Website";
  if (["t", "twitter", "x"].includes(compact) || text.includes("twitter")) return "Twitter";
  if (["g", "github"].includes(compact) || text.includes("github")) return "GitHub";
  if (["vikaas", "v"].includes(compact) || text.includes("vikaas")) return "Vikaas";
  if (["b", "w", "web", "website", "banao", "banaowebsite", "banaoweb"].includes(compact) || text.includes("banao")) return "Banao Website";
  return value || "Unknown";
}

export function leadStage(lead = {}) {
  return lead.stage || lead.workflow_status || lead.status || "New";
}

export function leadOwnerName(data, lead = {}) {
  return lead.owner_name || employeeName(data, lead.owner_id || lead.owner) || "Unassigned";
}

export function leadNoteCount(data, lead = {}) {
  if (lead.notes_count !== undefined) return lead.notes_count;
  return (data.leadNotes || []).filter((note) => String(note.lead) === String(lead.id)).length;
}

export function leadContactSummary(data, lead = {}, field) {
  const contacts = (data.leadContacts || []).filter((item) => String(item.lead) === String(lead.id));
  const direct = field === "email" ? lead.email || lead.contact_email : lead.phone || lead.contact_phone;
  return direct || contacts.map((item) => item[field]).filter(Boolean).join(", ") || "-";
}

export function sourceTone(label = "") {
  const text = String(label || "").toLowerCase();
  if (/interviewgod|interview/i.test(text)) return "interviewgod";
  if (/instagram|insta/i.test(text)) return "instagram";
  if (/linkedin|linked/i.test(text)) return "linkedin";
  if (/client/i.test(text)) return "client";
  if (/twitter/i.test(text)) return "twitter";
  if (/github/i.test(text)) return "github";
  if (/vikaas/i.test(text)) return "vikaas";
  if (/banao|website/i.test(text)) return "banao";
  return "dynamic";
}

export function priorityTone(value = "") {
  if (/urgent|high|hot/i.test(String(value))) return "red";
  if (/normal|medium/i.test(String(value))) return "gold";
  return "green";
}

export function marketingRecentEvents(data, leads = []) {
  const leadMap = indexById(leads);
  const activityRows = (data.leadActivities || []).map((item) => ({
    id: `activity-${item.id}`,
    title: item.activity_type || item.title || "Activity",
    lead: leadMap.get(String(item.lead))?.company_name || item.lead_name || "Lead",
    when: item.created_at || item.updated_at,
  }));
  const noteRows = (data.leadNotes || []).map((item) => ({
    id: `note-${item.id}`,
    title: item.title || "LeadNote",
    lead: leadMap.get(String(item.lead))?.company_name || item.lead_name || "Lead",
    when: item.created_at || item.updated_at,
  }));
  const proposalRows = (data.leadProposals || []).map((item) => ({
    id: `proposal-${item.id}`,
    title: item.title || "ProposalArtifact",
    lead: leadMap.get(String(item.lead))?.company_name || item.lead_name || "Lead",
    when: item.created_at || item.updated_at,
  }));
  return [...activityRows, ...noteRows, ...proposalRows].sort((left, right) => new Date(right.when || 0) - new Date(left.when || 0));
}

export function marketingSourceCards(leads = []) {
  const rowsBySource = groupBy(leads, leadSource);
  return Array.from(rowsBySource.entries()).map(([label, rows]) => ({
    label,
    count: rows.length,
    logo: String(label || "?").slice(0, 2).toUpperCase(),
    tone: sourceTone(label),
  }));
}

export function leadSourceCards(data, leads = []) {
  const counts = data.leadOriginCounts || {};

  const rowsByType = groupBy(leads, (l) => normalizeLeadSource(leadSource(l)));
  const countsByType = new Map();
  Object.entries(counts).forEach(([src, count]) => {
    const type = normalizeLeadSource(src);
    countsByType.set(type, (countsByType.get(type) || 0) + Number(count || 0));
  });

  const getFinalCount = (type) => (countsByType.get(type) || 0) || (rowsByType.get(type) || []).length;

  return [
    { label: "Banao Website", logo: "B", tone: "banao", count: getFinalCount("Banao Website") },
    { label: "Instagram", logo: "IG", tone: "instagram", count: getFinalCount("Instagram") },
    { label: "Client Website", logo: "CW", tone: "client", count: getFinalCount("Client Website") },
    { label: "LinkedIn", logo: "LI", tone: "linkedin", count: getFinalCount("LinkedIn") },
  ];
}

export function uniqueOptions(values) {
  return Array.from(new Set((values || []).filter(Boolean).map((value) => String(value))));
}

export function toPascalCase(value) {
  return String(value || "")
    .replace(/[_\-]+/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join("");
}

export function downloadCsv(filename, columns, rows) {
  const headers = (columns || []).map((column) => toPascalCase(column));
  const safeName = filename && filename.endsWith(".csv") ? filename : `${toPascalCase(filename || "Export")}.csv`;
  const csv = [headers, ...rows].map((row) => row.map(csvValue).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = safeName;
  link.click();
  URL.revokeObjectURL(url);
}
